/**
 * Centralized "return to the page you were on" support for the auth flow.
 *
 * Capture sites (the dashboard auth guard, the free-trial page's redirect to
 * sign-up, the course booking login/register prompt, the navbar login links)
 * attach the current internal path to the login or register URL as a
 * `?redirect=` param via `buildAuthHref`/`buildLoginHref`/`buildRegisterHref`,
 * and the two auth pages hand it to each other via `forwardReturnPath`. The
 * single navigation owner (AuthLayout) reads it back after a successful
 * sign-in via `getSafeRedirectParam` and navigates there instead of the
 * default dashboard; RegisterForm reads the same param for the sign-up half.
 *
 * Everything funnels through `sanitizeReturnPath`, which is the open-redirect
 * guard: only same-origin application paths are ever honored.
 */

/** Query-string key that carries the post-login return path. */
export const LOGIN_REDIRECT_PARAM = "redirect";

/**
 * Route keywords that must never be a return destination: sending the user
 * back to an auth screen after they just authenticated is at best pointless and
 * at worst a redirect loop. These keywords are only ever used for auth routes,
 * so a path-segment match is a reliable signal regardless of the
 * `/region/locale` prefix.
 */
const AUTH_PATH_SEGMENTS = [
  "login",
  "register",
  "verify",
  "forgot-password",
  "reset-password",
  "impersonate",
];

/**
 * True if the string contains any ASCII control character (0x00–0x1F or 0x7F).
 * Checked via char codes rather than a control-char regex literal so no control
 * bytes ever appear in source. These could otherwise smuggle a URL/header break
 * through a sloppy decode.
 */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }

  return false;
}

/**
 * Validates a candidate return path and returns it unchanged when safe, or
 * `null` when it must be rejected.
 *
 * Only relative, same-origin application paths are allowed. Everything that
 * could escape the origin — absolute URLs, protocol-relative `//host`,
 * backslash tricks browsers normalize to `/`, or embedded control characters —
 * is rejected, which is what prevents an open redirect (`?redirect=https://evil`).
 */
export function sanitizeReturnPath(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") {
    return null;
  }

  const value = raw.trim();

  if (value === "") {
    return null;
  }

  // Must be a root-relative path.
  if (!value.startsWith("/")) {
    return null;
  }

  // `//host` (protocol-relative) and `/\host` (browsers treat `\` as `/`) both
  // escape to another origin.
  if (value.startsWith("//") || value.startsWith("/\\")) {
    return null;
  }

  // No scheme, no backslashes, no control characters.
  if (value.includes("://") || value.includes("\\") || hasControlChars(value)) {
    return null;
  }

  // Never bounce back to an auth page (would loop or strand the user).
  if (isAuthPath(value)) {
    return null;
  }

  return value;
}

/**
 * Builds an auth-entry URL (login OR register), appending the sanitized current
 * location as a `?redirect=` param so the user is returned there once they are
 * authenticated. Falls back to the bare auth URL when there is no valid
 * destination to preserve.
 *
 * Register is included because signing up is the other half of the same
 * decision: a visitor sent to the free-trial page while logged out picks
 * whichever of the two applies to them, and both must come back to the page
 * that sent them.
 *
 * @param authHref      The already-localized auth path (e.g. `/mena/en/register`).
 * @param returnToPath  The current internal path+query to come back to.
 */
export function buildAuthHref(
  authHref: string,
  returnToPath: string | null | undefined,
): string {
  const safe = sanitizeReturnPath(returnToPath);

  if (!safe) {
    return authHref;
  }

  const separator = authHref.includes("?") ? "&" : "?";

  return `${authHref}${separator}${LOGIN_REDIRECT_PARAM}=${encodeURIComponent(safe)}`;
}

/**
 * Builds the login URL with a return path - see `buildAuthHref`, of which this
 * is the login-specific spelling kept for the existing capture sites.
 */
export function buildLoginHref(
  loginHref: string,
  returnToPath: string | null | undefined,
): string {
  return buildAuthHref(loginHref, returnToPath);
}

/**
 * Builds the register URL with a return path - see `buildAuthHref`, of which
 * this is the register-specific spelling.
 *
 * Used by capture sites that send a logged-out visitor straight to sign-up
 * rather than offering a login/register choice first (the free-trial booking
 * page): signing up is the expected action there, and anyone who already has
 * an account reaches login through the register page's own cross-link, which
 * forwards this same return path.
 */
export function buildRegisterHref(
  registerHref: string,
  returnToPath: string | null | undefined,
): string {
  return buildAuthHref(registerHref, returnToPath);
}

/**
 * Carries an existing `?redirect=` across the login <-> register cross-links.
 *
 * Those two pages link to each other ("no account? register", "already have an
 * account? login"), and a visitor who arrived at one of them from a booking
 * page and then switched to the other used to lose the return path at that hop
 * - which is what stranded them on the home page/dashboard after signing in.
 *
 * @param targetHref  The already-localized auth path being linked to.
 * @param search      The current query string (or URLSearchParams) to read from.
 */
export function forwardReturnPath(
  targetHref: string,
  search: string | URLSearchParams | null | undefined,
): string {
  return buildAuthHref(targetHref, getSafeRedirectParam(search));
}

/**
 * Reads and validates the `redirect` param from a query string (or a
 * `URLSearchParams`). Returns a safe return path or `null`.
 */
export function getSafeRedirectParam(
  search: string | URLSearchParams | null | undefined,
): string | null {
  if (search == null) {
    return null;
  }

  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;

  return sanitizeReturnPath(params.get(LOGIN_REDIRECT_PARAM));
}

/**
 * True when any segment of the path is an auth keyword — see AUTH_PATH_SEGMENTS.
 */
function isAuthPath(path: string): boolean {
  const pathname = path.split(/[?#]/, 1)[0];
  const segments = pathname.split("/").filter(Boolean);

  return segments.some((segment) => AUTH_PATH_SEGMENTS.includes(segment));
}
